import {graphql, Link, PageProps} from 'gatsby';
import * as React from 'react';
import {Helmet} from 'react-helmet-async';
import {Trans, useTranslation} from 'react-i18next';

import Hero from '@src/components/Hero';
import PageHero from '@src/components/Hero/PageHero';
import {useNow} from '@src/components/NowProvider';
import PageLayout from '@src/components/PageLayout';
import PageTheme from '@src/components/PageTheme';
import CountyForm, {
  CountyFormProps,
} from '@src/components/PlanFinder/CountyForm';
import {getLanguageByCode} from '@src/i18n/utils';
import Couple from '@src/images/couplewalking.png';
import {GTMCallUs} from '@src/components/GTM/GTMCallUs';

import {activePlanYear} from '@src/utils';

import type {
  PlanFinderLocationsQuery,
  SpanishPlanFinderLocationsQuery,
} from '@graphql-types';

const GTM_DEFAULT_KEYS = {
  phoneNumber: 'needHelp.number',
  text: 'needHelp.text',
};

const GTM_DIRECT_KEYS = {
  phoneNumber: 'needHelpDirect.number',
  text: 'needHelpDirect.text',
};

const GTM_CALL_US_PROPS = {
  defaultKeys: GTM_DEFAULT_KEYS,
  directKeys: GTM_DIRECT_KEYS,
  namespace: 'PlanFinder',
};

const PlanFinderIndex = ({
  data: {
    allLanguages: {nodes: languages},
    allPbps: {nodes: planBenefitPackages},
    allPrismicMainNavigation: navigation,
    allPrismicNotifications: notifications,
    site: {
      siteMetadata: {
        lastBuildTime,
        siteName,
        siteUrl,
        ssaCountiesForPbps: ssaCounties,
        supportedCounties,
      },
    },
  },
  location,
}: PageProps<PlanFinderLocationsQuery | SpanishPlanFinderLocationsQuery>) => {
  const translations = [
    {href: '/find-a-plan/', lang: getLanguageByCode('en-us', languages)},
    {
      href: '/es/encontrar-un-plan/',
      lang: getLanguageByCode('es-mx', languages),
    },
  ];

  return (
    <PageLayout
      {...PageLayout.dateProps(lastBuildTime)}
      languages={languages}
      location={location}
      navigation={navigation}
      notifications={notifications}
      siteName={siteName}
      siteUrl={siteUrl}
      translations={translations}>
      <PlanFinderIndexComponent
        planBenefitPackages={planBenefitPackages}
        ssaCounties={ssaCounties}
        supportedCounties={supportedCounties}
      />
    </PageLayout>
  );
};

// use an inner component so we can get "now" from the provider inserted in PageLayout
const PlanFinderIndexComponent: React.FC<
  Pick<
    CountyFormProps,
    'planBenefitPackages' | 'ssaCounties' | 'supportedCounties'
  >
> = props => {
  const now = useNow();
  const {t} = useTranslation('PlanFinder');

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <PageTheme theme="white">
        <Hero>
          <PageHero>
            <Trans i18nKey="pageHeading" parent="h1" t={t}>
              Let’s find your Devoted plan.
            </Trans>
            <div className="row">
              <div className="col">
                <CountyForm
                  activePlanYear={activePlanYear(now)}
                  planBenefitPackages={props.planBenefitPackages}
                  ssaCounties={props.ssaCounties}
                  supportedCounties={props.supportedCounties}
                />

                <GTMCallUs {...GTM_CALL_US_PROPS}>
                  {({text, phoneNumber}) => (
                    <p>
                      {text} <strong>{phoneNumber} (TTY 711)</strong>{' '}
                      {t('salesEventLearnMore')}{' '}
                      <Link to="/sales-events" style={{color: '#000'}}>
                        {t('salesEventLink')}
                      </Link>
                      .
                    </p>
                  )}
                </GTMCallUs>
              </div>
              <div className="col">
                <img src={Couple} alt="Couple" />
              </div>
            </div>
          </PageHero>
        </Hero>
      </PageTheme>
    </>
  );
};

export default PlanFinderIndex;

export const query = graphql`
  query PlanFinderLocations {
    ...PageLayout

    allPbps {
      nodes {
        csnp_conditions {
          type
        }
        name
        plan {
          ma_variant
          plan_type
        }
        record_locator
        service_areas {
          county_code
        }
        year
      }
    }
    site {
      siteMetadata {
        ssaCountiesForPbps {
          code
          display_name
          state_abbreviation
          zip_codes {
            zip_code
          }
        }
        supportedCounties {
          county_code
          pages {
            lang
            path
          }
          slug
        }
      }
    }
  }
`;
